'use client';

import { useEffect, useRef, useState } from 'react';

export type HeBtnOption = {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  onSelect: () => void;
};

export type HeBtnProps = {
  /** Default primary label, e.g. "Review & send". */
  label: string;
  /** Optional leading icon inside the primary button. */
  leadingIcon?: React.ReactNode;
  /**
   * Hunter recommendation options shown in the caret dropdown.
   * Canonical order: Review & [verb] (espresso) · Remind me later (text-2)
   * · No thanks (text-3). At least one option required.
   */
  options: HeBtnOption[];
  /** Click handler for the primary button (the "Review & verb" path). */
  onPrimary: () => void;
  /** Visual size — "sm" omits the box-shadow + uses tighter padding. */
  size?: 'md' | 'sm';
  /** Pop direction. Defaults to "up" (per canon). */
  popDirection?: 'up' | 'down';
  className?: string;
};

const CaretIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * He-btn (Hunter split-button) — canonical primary action for any Hunter
 * recommendation. Default click = Review & [verb]; caret opens the
 * three-option menu (Review & verb · Remind me later · No thanks).
 *
 * Canon: contracts/designv2/respond_v2.css `.he-btn` (used by every surface).
 */
export function HeBtn({
  label,
  leadingIcon,
  options,
  onPrimary,
  size = 'md',
  popDirection = 'up',
  className,
}: HeBtnProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('keydown', esc);
    };
  }, [open]);

  const popStyle: React.CSSProperties =
    popDirection === 'down'
      ? { top: '100%', bottom: 'auto', marginTop: 8, marginBottom: 0 }
      : {};

  return (
    <div ref={rootRef} className={`he-btn${size === 'sm' ? ' sm' : ''}${className ? ' ' + className : ''}`}>
      <button type="button" className="primary" onClick={onPrimary}>
        {leadingIcon ? <span className="lead">{leadingIcon}</span> : null}
        {label}
      </button>
      <span className="divider" aria-hidden />
      <button
        type="button"
        className="caret"
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {CaretIcon}
      </button>

      {open ? (
        <div className="he-btn-pop" role="menu" style={popStyle}>
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="menuitem"
              className="he-btn-opt"
              onClick={() => {
                setOpen(false);
                opt.onSelect();
              }}
            >
              {opt.icon ? <span className="ic">{opt.icon}</span> : null}
              <span>
                <span style={{ display: 'block' }}>{opt.label}</span>
                {opt.description ? (
                  <span style={{ display: 'block', font: '400 11px/1.3 Inter', color: 'var(--text-3)', marginTop: 2 }}>
                    {opt.description}
                  </span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
